import { useCurrentUser } from '@/hooks/useCurrentUser';
import { NotificationItem, RowActionKind, ToolbarAction, TypeFilter } from '@/components/NotificationTable/types';
import { ProviderMonitorDropdown } from '@/components/ProviderMonitorDropdown';
import { FEATURE_FLAGS, TaskStatus } from '@bariendo/lib/constants';
import { useFeatureFlagEnabled } from '@/hooks/useFeatureFlag';
import {
  markNotificationAsRead,
  markNotificationsAsArchived,
  markNotificationsAsRead,
  markNotificationsAsUnread,
} from '@bariendo/lib/notifications';
import { Alert, Button, Collapse, Container, Group, Stack, Title } from '@mantine/core';
import { createReference, getReferenceString } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { IconArchive, IconBell, IconEye, IconPlus } from '@tabler/icons-react';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { CommunicationTypeFilter } from '../../components/NotificationTable/CommunicationTypeFilter';
import { NotificationTable } from '../../components/NotificationTable/NotificationTable';
import { useNotificationTable } from '../../components/NotificationTable/useNotificationTable';
import { SearchInput } from '../../components/SearchInput';
import { InboxCreateTaskForm } from './InboxCreateTaskForm';
import { InboxSortDropdown } from '@/components/InboxSort';
import { FutureTasksToggle } from '@/components/InboxFilters';
import { useInboxSort } from '@/hooks/useInboxSort';
import { useInboxTracking } from '../../hooks/useInboxTracking';
import { useMedplumProfile } from '@medplum/react';

// Create stable array to prevent unnecessary re-renders
const INBOX_NOTIFICATION_STATUSES = [TaskStatus.READY, TaskStatus.IN_PROGRESS];

export function InboxPage(): ReactElement {
  const medplum = useMedplum();
  const posthog = usePostHog();
  const {
    currentUser,
    currentUserPermissions: { isAdmin },
  } = useCurrentUser();
  const [selectedProvider, setSelectedProvider] = useState<Practitioner | null>(null);
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [sortMode, setSortMode] = useInboxSort(profile?.id);

  const [showCreateTask, setShowCreateTask] = useState(false);
  // Session-only on purpose: every visit opens on today's work (BAR-2679).
  const [showFutureTasks, setShowFutureTasks] = useState(false);

  // Every other task surface checks this flag (the type filter's own label,
  // the table's task rows, the patient tab, the timeline). Without it here,
  // a disabled-flag environment still reaches task CREATION through the
  // empty-labelled Tasks segment, which remains clickable when its label
  // renders blank.
  const isTaskManagementEnabled = useFeatureFlagEnabled(FEATURE_FLAGS.ENABLE_TASK_MANAGEMENT);

  const {
    medplum: _medplum,
    typeFilter,
    setTypeFilter,
    searchTerm,
    setSearchTerm,
    isSearching,
    notifications,
    loading,
    loadingMore,
    refresh,
    handleScroll,
  } = useNotificationTable({
    notificationStatus: INBOX_NOTIFICATION_STATUSES,
    selectedProvider,
    sortMode,
    // The toggle only renders behind the flag (NotificationTable gates the
    // sort-control slot on it), while clinical tasks load regardless. With
    // the flag off there would be no way back, so don't hide anything.
    showFutureTasks: showFutureTasks || !isTaskManagementEnabled,
    loadAllClinicalTasks: true,
  });

  const isTasksView = typeFilter === TypeFilter.TASKS && isTaskManagementEnabled;
  // Only views that can hold clinical tasks get the future-tasks toggle, and
  // not while searching: the filter is suspended for a search, so the chip
  // would be a no-op.
  const showFutureTasksToggle = (typeFilter === TypeFilter.ALL || isTasksView) && !isSearching;

  // The create form belongs to the Tasks view: leaving it would strand an
  // open form in a view whose toolbar no longer offers the button, and whose
  // filtered list would not show the created task anyway.
  useEffect(() => {
    if (!isTasksView) {
      setShowCreateTask(false);
    }
  }, [isTasksView]);

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        id: 'mark-as-read',
        label: 'Mark {count} as read',
        icon: <IconBell size={14} />,
        color: 'blue',
        isVisible: ({ selectedCount, selectedNotifications }) =>
          selectedCount > 0 && selectedNotifications.some((n) => !n.isRead),
        handler: async (medplum, selectedRefs, onComplete) => {
          await markNotificationsAsRead(medplum, selectedRefs);
          onComplete();
        },
      },
      {
        id: 'mark-as-unread',
        label: 'Mark {count} as unread',
        icon: <IconBell size={14} />,
        color: 'blue',
        isVisible: ({ selectedCount, selectedNotifications }) =>
          selectedCount > 0 && selectedNotifications.some((n) => n.isRead),
        handler: async (medplum, selectedRefs, onComplete) => {
          await markNotificationsAsUnread(medplum, selectedRefs);
          onComplete();
        },
      },
      {
        id: 'archive',
        // In the Tasks view, archiving a clinical task IS completing it
        // (full guarded protocol incl. completed-by stamp) — say so.
        label: isTasksView ? 'Complete {count}' : 'Archive {count} items',
        icon: <IconArchive size={14} />,
        color: 'gray',
        handler: async (medplum, selectedRefs, onComplete) => {
          await markNotificationsAsArchived(medplum, selectedRefs);
          onComplete();
        },
      },
    ],
    [isTasksView]
  );

  const { handleRowClick: trackRowClick } = useInboxTracking({
    posthog,
    page: 'inbox',
    typeFilter,
    searchTerm,
    notifications,
    selectedProvider,
    currentUser: currentUser ?? null,
  });

  const handleRowClick = useCallback(
    (notification: NotificationItem, medplum: any, defaultHandler: (notification: NotificationItem) => void) => {
      // Track and handle row click
      trackRowClick(notification, defaultHandler);

      // Mark as read if unread
      if (!notification.isRead) {
        const notificationRef = createReference(notification.task);
        // Pass the loaded Task so the helper doesn't re-read it per click.
        markNotificationAsRead(medplum, notificationRef, notification.task);
      }
    },
    [trackRowClick]
  );

  const isMonitoringOther =
    selectedProvider && currentUser && getReferenceString(selectedProvider) !== getReferenceString(currentUser);

  return (
    <Container
      size="xl"
      py="md"
      style={{
        height: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="lg" style={{ flex: 1, overflow: 'hidden' }}>
        <Group align="center">
          <Title order={1}>Inbox</Title>
        </Group>

        <Collapse in={!!isMonitoringOther}>
          <Alert icon={<IconEye size={16} />} color="blue" variant="light" mb="lg">
            You're viewing{' '}
            <strong>
              {selectedProvider?.name?.[0]?.given?.join(' ')} {selectedProvider?.name?.[0]?.family}
            </strong>
            's inbox. Restricted content opens in the patient's timeline.
          </Alert>
        </Collapse>

        <Group justify="space-between" align="center">
          <CommunicationTypeFilter value={typeFilter} onChange={setTypeFilter} />
          <Group gap="sm">
            <SearchInput value={searchTerm} onChange={setSearchTerm} />
            {isTasksView && (
              <Button color="green" leftSection={<IconPlus size={16} />} onClick={() => setShowCreateTask(true)}>
                New task
              </Button>
            )}
            {isAdmin && (
              <ProviderMonitorDropdown selectedProvider={selectedProvider} onProviderChange={setSelectedProvider} />
            )}
          </Group>
        </Group>

        {showCreateTask && (
          <InboxCreateTaskForm
            onCancel={() => setShowCreateTask(false)}
            onSave={() => {
              setShowCreateTask(false);
              // A task assigned to me or my role belongs in this list now.
              void refresh();
            }}
          />
        )}

        <NotificationTable
          medplum={medplum}
          notifications={notifications}
          loading={loading}
          loadingMore={loadingMore}
          refresh={refresh}
          handleScroll={handleScroll}
          rowActionKind={RowActionKind.ARCHIVE}
          toolbarActions={toolbarActions}
          inboxSortControl={
            <>
              {showFutureTasksToggle && <FutureTasksToggle value={showFutureTasks} onChange={setShowFutureTasks} />}
              <InboxSortDropdown value={sortMode} onChange={setSortMode} />
            </>
          }
          searchTerm={searchTerm}
          onRowClick={handleRowClick}
        />
      </Stack>
    </Container>
  );
}
